import React, { useState } from 'react';
import Icon   from '../components/ui/Icon';
import Button from '../components/ui/Button';
import { useAuth } from '../context/AuthContext';

const STEPS = [
  { icon: 'user',     label: 'Account Created',   sub: 'Your details have been saved',       done: true  },
  { icon: 'mail',     label: 'Email Verified',     sub: 'OTP verified successfully',           done: true  },
  { icon: 'settings', label: 'Admin Review',       sub: 'Our team is reviewing your profile',  done: false, active: true },
  { icon: 'check',    label: 'Access Granted',     sub: 'Full app access unlocked',            done: false },
];

const PendingApprovalPage = () => {
  const { logout, pendingData } = useAuth();
  const [copied, setCopied] = useState(false);

  const email = pendingData?.email ?? pendingData?.name ?? '';

  const handleCopy = () => {
    navigator.clipboard?.writeText('support@hostassist.in');
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="page-scroll flex flex-col min-h-full bg-surface-900 relative overflow-hidden">
      <div className="noise-overlay" />

      {/* Ambient glows */}
      <div
        className="absolute -top-20 -right-20 w-64 h-64 rounded-full opacity-[0.07] pointer-events-none"
        style={{ background: 'radial-gradient(circle, #6366f1, transparent 70%)' }}
      />
      <div
        className="absolute bottom-0 -left-24 w-60 h-60 rounded-full opacity-[0.05] pointer-events-none"
        style={{ background: 'radial-gradient(circle, #818cf8, transparent 70%)' }}
      />

      <div className="flex-1 flex flex-col px-6 pt-16 pb-10">

        {/* Hero icon */}
        <div className="flex flex-col items-center text-center mb-8 animate-fadeUp" style={{ animationFillMode: 'forwards' }}>
          <div className="relative mb-6">
            {/* Outer pulse ring */}
            <div
              className="absolute inset-0 rounded-full animate-ping opacity-20"
              style={{ background: 'radial-gradient(circle, #6366f1, transparent 70%)', animationDuration: '2s' }}
            />
            <div
              className="w-24 h-24 rounded-3xl flex items-center justify-center brand-glow relative z-10"
              style={{ background: 'linear-gradient(135deg, #4338ca 0%, #6366f1 60%, #818cf8 100%)' }}
            >
              <span className="text-4xl">⏳</span>
            </div>
          </div>

          <div
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-4"
            style={{ background: 'rgba(99,102,241,0.10)', border: '1px solid rgba(99,102,241,0.20)' }}
          >
            <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" />
            <span className="font-dm text-xs text-brand-600 font-medium">Approval Pending</span>
          </div>

          <h1 className="font-syne font-extrabold text-slate-800 text-2xl leading-tight mb-3">
            Registration Received!
          </h1>
          <p className="font-dm text-surface-200 text-sm leading-relaxed max-w-[280px]">
            Thank you for signing up. Our team will review your host application and get back to you shortly.
          </p>

          {email && (
            <div className="mt-3 flex items-center gap-2 px-4 py-2 rounded-xl bg-surface-700 border border-surface-500">
              <Icon name="mail" size={14} className="text-brand-500" />
              <span className="font-dm text-xs text-slate-600">{email}</span>
            </div>
          )}
        </div>

        {/* Progress steps */}
        <div
          className="rounded-3xl p-5 mb-5 card-shadow animate-fadeUp s2"
          style={{ animationFillMode: 'forwards', opacity: 0, background: '#ffffff', border: '1px solid #e2e8f0' }}
        >
          <p className="font-syne font-bold text-slate-800 text-sm mb-4">Application Status</p>
          <div className="flex flex-col gap-0">
            {STEPS.map((step, i) => (
              <div key={step.label} className="flex gap-3">
                {/* Timeline */}
                <div className="flex flex-col items-center">
                  <div
                    className={`w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0 transition-all ${
                      step.done
                        ? 'bg-emerald-500'
                        : step.active
                        ? 'bg-amber-400'
                        : 'bg-surface-600 border border-surface-400'
                    }`}
                  >
                    {step.done ? (
                      <Icon name="check" size={14} className="text-white" />
                    ) : step.active ? (
                      <span className="w-2 h-2 rounded-full bg-white animate-pulse" />
                    ) : (
                      <Icon name={step.icon} size={14} className="text-surface-300" />
                    )}
                  </div>
                  {i < STEPS.length - 1 && (
                    <div
                      className={`w-px flex-1 my-1 min-h-[20px] ${step.done ? 'bg-emerald-300' : 'bg-surface-500'}`}
                    />
                  )}
                </div>

                {/* Content */}
                <div className="pb-4 pt-1 flex-1">
                  <p className={`font-dm text-sm font-semibold leading-tight ${
                    step.done ? 'text-emerald-600' : step.active ? 'text-amber-600' : 'text-surface-300'
                  }`}>
                    {step.label}
                  </p>
                  <p className="font-dm text-xs text-surface-200 mt-0.5">{step.sub}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Info card */}
        <div
          className="rounded-2xl p-4 mb-6 animate-fadeUp s3"
          style={{ animationFillMode: 'forwards', opacity: 0, background: 'rgba(99,102,241,0.06)', border: '1px solid rgba(99,102,241,0.15)' }}
        >
          <div className="flex gap-3">
            <div className="w-8 h-8 rounded-xl bg-brand-100 flex items-center justify-center flex-shrink-0">
              <Icon name="bell" size={14} className="text-brand-600" />
            </div>
            <div>
              <p className="font-dm text-xs font-semibold text-slate-700 mb-1">What happens next?</p>
              <p className="font-dm text-xs text-surface-200 leading-relaxed">
                You'll receive an email notification once your account is approved. This usually takes <span className="text-slate-700 font-medium">1–2 business days</span>.
              </p>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-col gap-3 animate-fadeUp s4" style={{ animationFillMode: 'forwards', opacity: 0 }}>
          <button
            onClick={handleCopy}
            className="w-full flex items-center justify-between px-4 py-3.5 rounded-2xl border border-surface-400 bg-surface-700 press-scale transition-all"
          >
            <div className="flex items-center gap-3">
              <Icon name="mail" size={16} className="text-brand-500" />
              <div className="text-left">
                <p className="font-dm text-xs font-semibold text-slate-700">Need help? Contact us</p>
                <p className="font-dm text-xs text-surface-200">support@hostassist.in</p>
              </div>
            </div>
            <span className={`font-dm text-xs font-medium transition-colors ${copied ? 'text-emerald-600' : 'text-brand-600'}`}>
              {copied ? '✓ Copied' : 'Copy'}
            </span>
          </button>

          <Button variant="ghost" size="lg" onClick={logout}>
            <Icon name="arrowLeft" size={16} />
            Back to Login
          </Button>
        </div>

      </div>
    </div>
  );
};

export default PendingApprovalPage;
